import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { TextField, Button, Paper, Box, Divider, Alert, CircularProgress, Stack, Typography } from "@mui/material";
import {API_URL,API_UPLOAD_URL} from "./config";
import { startAuthentication, browserSupportsWebAuthn } from "@simplewebauthn/browser";

function getCampusFromHost(hostname) {
  // localhost / ip -> no campus
  if (!hostname) return "";
  if (hostname === "localhost" || hostname === "127.0.0.1") return "";

  const parts = hostname.split(".");
  // campus.unifloo.com => ["campus","unifloo","com"] => campus
  if (parts.length === 3 && parts[1] === "unifloo" && parts[2] === "com") {
    return parts[0].toUpperCase();
  }
  // www.unifloo.com => no campus
  return "";
}

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const hostCampus = useMemo(
    () => getCampusFromHost(window.location.hostname),
    []
  );
  const isCampusLocked = Boolean(hostCampus); // true when on campus subdomain

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [campusCode, setCampusCode] = useState(hostCampus || "");
  const [authToken, setAuthToken] = useState("");
  const [error, setError] = useState("");
  const [showAuthenticator, setShowAuthenticator] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [isRegisteringAuthenticator, setIsRegisteringAuthenticator] = useState(false);
  const [isVerifyingAuthenticator, setIsVerifyingAuthenticator] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  // If you navigated here with state (pre-fill fields), apply it.
  // But if campus is locked by subdomain, ALWAYS force the host campus.
  useEffect(() => {
    if (location.state) {
      setUsername(location.state.username || "");
      setPassword(location.state.password || "");
      if (!isCampusLocked) {
        setCampusCode(location.state.campusCode || "");
      }
    }
    if (isCampusLocked) {
      setCampusCode(hostCampus);
    }
  }, [location.state, isCampusLocked, hostCampus]);

  // Helper: build endpoint safely regardless of API_URL being "" or "/api"
  const apiPost = (path, body) => {
    // If API_URL="/api", path should start with "/auth/.."
    // If API_URL="", path should start with "/api/auth/.."
    const base = (API_URL || "").replace(/\/+$/, "");
    const p = path.startsWith("/") ? path : `/${path}`;
    return axios.post(`${base}${p}`, body);
  };

  // Step 1: Login
   // Helpers
const toBool = (v) => {
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "1" || s === "true" || s === "yes";
  }
  return false;
};

const normalizeRoles = (roleField) => {
  if (!roleField) return [];

  // if backend sends array
  if (Array.isArray(roleField)) {
    return roleField.map((r) => String(r).trim()).filter(Boolean);
  }

  // string: support comma/pipe/semicolon combos
  const s = String(roleField).trim();
  if (!s) return [];

  return s.split(/[,;|]+/).map((r) => r.trim()).filter(Boolean);
};

const isOnlyStudent = (roleField) => {
  const roles = normalizeRoles(roleField).map((r) => r.toLowerCase());
  return roles.length === 1 && roles[0] === "student";
};

// Step 1: Login (UPDATED)
const handleLogin = async (e) => {
  e.preventDefault();
  setError("");
  setQrCode("");
  setShowAuthenticator(false);
  setAuthToken("");
  setIsRegisteringAuthenticator(false);
  setIsVerifyingAuthenticator(false);

  const effectiveCampus = (isCampusLocked ? hostCampus : campusCode)
    .trim()
    .toUpperCase();

  try {
    const loginPath = API_URL === "/api" ? "/auth/login" : "/api/auth/login";

    const response = await apiPost(loginPath, {
      username,
      password,
      campusCode: effectiveCampus,
      // ❌ DO NOT send is_2fa_enabled from frontend
    });

    const user = response.data?.user || {};
    const needsRegistration = !!response.data?.requiresAuthenticatorRegistration;
    const needsOtp = !!response.data?.requiresAuthenticator;

    // Prefer roles array if you add it later; else use role string
    const roleField = user.roles ?? user.role;
    const onlyStudent = isOnlyStudent(roleField);

    // ✅ RULE #1: if 2FA enabled => ALWAYS enforce (register or OTP)
    const is2FAEnabled = toBool(user.is_2fa_enabled);
    if (is2FAEnabled) {
      if (needsRegistration) {
        setIsRegisteringAuthenticator(true);
        await registerAuthenticator(effectiveCampus);
        return;
      }
      if (needsOtp) {
        setShowAuthenticator(true);
        setIsVerifyingAuthenticator(true);
        return;
      }

      // If backend returns token even though 2FA enabled => block and show error
      setError(
        "2FA is enabled for this account, but /login did not request OTP/registration. Fix backend /login to return requiresAuthenticator* when is_2fa_enabled=1."
      );
      return;
    }

    // ✅ RULE #2: 2FA NOT enabled => Student-only can bypass (must get token)
    if (onlyStudent) {
      if (response.data?.token) {
        localStorage.setItem("auth", response.data.token);
        navigate("/home");
        return;
      }
      setError(
        "Student-only login expected a token, but none was returned. Update backend /login to issue token for Student-only when 2FA is disabled."
      );
      return;
    }

    // ✅ RULE #3: everyone else (mixed/non-student) => enforce 2FA based on backend flags
    if (needsRegistration) {
      setIsRegisteringAuthenticator(true);
      await registerAuthenticator(effectiveCampus);
      return;
    }
    if (needsOtp) {
      setShowAuthenticator(true);
      setIsVerifyingAuthenticator(true);
      return;
    }

    // fallback: if backend decides to issue token directly
    if (response.data?.token) {
      localStorage.setItem("auth", response.data.token);
      navigate("/home");
      return;
    }

    setError("Unexpected login response.");
  } catch (err) {
    setError(err.response?.data?.message || "Login failed");
  }
};

  // Step 2: Register Authenticator (show QR)
  const registerAuthenticator = async (effectiveCampus) => {
    try {
      const registerPath = API_URL === "/api" ? "/auth/register-authenticator" : "/api/auth/register-authenticator";
      //const registerPath = API_URL;

      const response = await apiPost(registerPath, { username, campusCode: effectiveCampus });
      setQrCode(response.data.qrCode);
      setShowAuthenticator(true);
      setIsRegisteringAuthenticator(true);
      setIsVerifyingAuthenticator(false);
    } catch (err) {
      setError("Failed to register Authenticator");
    }
  };

  // Step 3: Verify code after scanning QR
  const handleVerifyRegisterAuthenticator = async () => {
    const effectiveCampus = (isCampusLocked ? hostCampus : campusCode).trim().toUpperCase();

    try {
      const verifyRegPath = API_URL === "/api" ? "/auth/verify-register-authenticator" : "/api/auth/verify-register-authenticator";
      //const verifyRegPath = API_URL;

      await apiPost(verifyRegPath, {
        username,
        campusCode: effectiveCampus,
        token: authToken,
      });

      setIsRegisteringAuthenticator(false);
      setIsVerifyingAuthenticator(true);
      setAuthToken("");
      setError("");
      alert("Authenticator registered! Now please enter your code again to log in.");
    } catch (err) {
      setError("Invalid authentication code for registration.");
    }
  };

  // Step 4: Regular OTP Verification for Login
  const handleVerifyAuthenticator = async () => {
    const effectiveCampus = (isCampusLocked ? hostCampus : campusCode).trim().toUpperCase();

    try {
      const verifyLoginPath = API_URL === "/api" ? "/auth/verify-login" : "/api/auth/verify-login";
      //const verifyLoginPath = API_URL;

      const response = await apiPost(verifyLoginPath, {
        username,
        password,
        campusCode: effectiveCampus,
        token: authToken,
      });
      localStorage.setItem("auth", response.data.token);
      navigate("/home");
    } catch (err) {
      setError("Invalid authentication code");
    }
  };


  const handlePasskeyLogin = async () => {
    setError("");

    if (!browserSupportsWebAuthn()) {
      setError("Passkeys are not supported by this browser or device.");
      return;
    }

    setPasskeyLoading(true);

    try {
      const base = (API_URL || "").replace(/\/+$/, "");
      const passkeyBase = API_URL === "/api" ? base : `${base}/api`;

      // No username, password, or campus is required.
      const optionsResponse = await axios.post(
        `${passkeyBase}/passkeys/authenticate/options`,
        {}
      );

      const { challengeId, options } = optionsResponse.data || {};

      if (!challengeId || !options) {
        throw new Error("Invalid passkey options response.");
      }

      const authenticationResponse = await startAuthentication({
        optionsJSON: options,
      });

      const verifyResponse = await axios.post(
        `${passkeyBase}/passkeys/authenticate/verify`,
        {
          challengeId,
          response: authenticationResponse,
          device: {
            platform: navigator.platform || "",
            userAgent: navigator.userAgent || "",
          },
        }
      );

      const token =
        verifyResponse.data?.token ||
        verifyResponse.data?.accessToken;

      if (!token) {
        throw new Error(
          "Passkey login succeeded, but no session token was returned."
        );
      }

      localStorage.setItem("auth", token);

      if (verifyResponse.data?.refreshToken) {
        localStorage.setItem(
          "refreshToken",
          verifyResponse.data.refreshToken
        );
      }

      navigate("/home");
    } catch (err) {
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error;

      if (err?.name === "NotAllowedError") {
        setError(
          "Passkey sign-in was cancelled, timed out, or no matching passkey is available on this device."
        );
      } else {
        setError(
          serverMessage ||
            err?.message ||
            "Passkey sign-in failed."
        );
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <Paper style={{ maxWidth: "400px", margin: "50px auto", padding: "20px" }}>
      <h2>Login</h2>

      {!showAuthenticator && (
        <Box component="form" onSubmit={handleLogin}>
          <Stack spacing={1.5}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Sign in to UniFLOO
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Sign in with a registered passkey without entering a username, password, or campus code. You can also use your password and 2FA.
            </Typography>

            <TextField
              label="Username"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />

            <TextField
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <TextField
              label="Campus Code"
              fullWidth
              value={campusCode}
              onChange={(e) => setCampusCode(e.target.value)}
              required
              disabled={isCampusLocked}
              helperText={
                isCampusLocked
                  ? `Campus locked from URL (${hostCampus}.unifloo.com)`
                  : "Enter your Campus Code"
              }
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              disabled={passkeyLoading}
              sx={{ fontWeight: 800, textTransform: "none", py: 1.1 }}
            >
              Login with Password
            </Button>

            <Divider>or</Divider>

            <Button
              variant="outlined"
              fullWidth
              type="button"
              onClick={handlePasskeyLogin}
              //disabled={passkeyLoading}
              sx={{ fontWeight: 800, textTransform: "none", py: 1.1 }}
            >
              {passkeyLoading ? <CircularProgress size={20} /> : "Login with Passkey"}
            </Button>

            <Typography variant="caption" color="text.secondary" align="center">
              No account details required. Your saved passkey identifies your UniFLOO account using Face ID, Touch ID, Windows Hello, or your device screen lock.
            </Typography>
          </Stack>
        </Box>
      )}

      {showAuthenticator && isRegisteringAuthenticator && (
        <div>
          <p>
            Download any Authenticator app (Ex: Microsoft Authenticator/Google Authenticator)
            on your mobile and scan this QR code.
          </p>
          {qrCode && <img src={qrCode} alt="Google Authenticator QR Code" />}
          <TextField
            style={{ margin: "20px 0" }}
            label="Enter code from app"
            fullWidth
            value={authToken}
            onChange={(e) => setAuthToken(e.target.value)}
            required
          />
          {error && <p style={{ color: "red" }}>{error}</p>}
          <Button variant="contained" color="primary" onClick={handleVerifyRegisterAuthenticator} fullWidth>
            Verify Authenticator Code
          </Button>
        </div>
      )}

      {showAuthenticator && isVerifyingAuthenticator && (
        <div>
          <TextField
            style={{ margin: "20px 0" }}
            label="Authentication Code"
            fullWidth
            value={authToken}
            onChange={(e) => setAuthToken(e.target.value)}
            required
          />
          {error && <p style={{ color: "red" }}>{error}</p>}
          <Button variant="contained" color="primary" onClick={handleVerifyAuthenticator} fullWidth>
            Verify & Login
          </Button>
        </div>
      )}
    </Paper>
  );
};

export default Login;
